import { defineComponent as _defineComponent } from 'vue'
import { type PropType, onUnmounted, ref, toRefs, useContext } from "@nuxtjs/composition-api";
import { type SelfDescribingEvent } from "@snowplow/browser-tracker";

import { useSnowplow } from "@/composables";
import { SECOND } from "@/utils/enums/time";
import logger, { logLevel } from "@/utils/logger";
import { VISIBLE_THRESHOLD_PERCENT } from "@/utils/snowplow/config";

declare module "@nuxt/types" {
  interface Context {
    _errored?: boolean;
    _redirected?: boolean;
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TrackImpression',
  props: {
  payload: {
    type: Object as PropType<SelfDescribingEvent>,
    required: true,
  },
  impressionTimeout: {
    type: Number,
    required: false,
    default: SECOND,
  },
  observerOptions: {
    type: Object,
    required: false,
    default: () => ({
      root: null, // use the default viewport
      rootMargin: "-80px 0px 0px 0px", // ccs style margins to account for our navBar
      threshold: [0, VISIBLE_THRESHOLD_PERCENT, 1], // fire observer callback at [0%, 50%, 100%]
    }),
  },
},
  setup(__props) {

const props = __props;



const { payload } = toRefs(props);

const context = useContext();
const snowplow = useSnowplow();
const observer = ref<IntersectionObserver>();
const timer = ref<Timer>();
const hasEventFired = ref(false);
const hasElBeenOffScreen = ref(false);
const hasError = ref<boolean | undefined>(context._errored); // hack until we move to Nuxt 3 and can leverage useError()

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
  if (timer.value) {
    clearTimeout(timer.value);
  }
});

const observerCallback: IntersectionObserverCallback = (entries, intersectionObserver): void => {
  // Save a reference to the observer, so we can disconnect it onUnmounted()
  if (!observer.value) {
    observer.value = intersectionObserver;
  }

  // Grab the last entry from the array, as it will be the most up-to-date
  const { intersectionRatio } = entries.pop() as IntersectionObserverEntry;

  // If we have met the visibility threshold
  if (intersectionRatio >= VISIBLE_THRESHOLD_PERCENT) {
    // Early return if we already have a timer (or) we've fired, and haven't gone offscreen yet.
    if (timer.value || (hasEventFired.value === true && hasElBeenOffScreen.value === false)) {
      return;
    }

    // Crate a setTimeout that will fire our event, and save the instance to timer.value
    timer.value = setTimeout(() => {
      try {
        snowplow.trackEvent(payload.value);
        timer.value = undefined;
        hasEventFired.value = true;
      } catch (error) {
        logger(logLevel.ERROR, "Error sending snowplow event", { error, payload: payload.value });
      }
    }, props.impressionTimeout);
  } else {
    // We are no longer intersecting, so we clear the timer
    if (timer.value) {
      clearTimeout(timer.value);
      timer.value = undefined;
    }

    // If the event has already fired, and we are now at 0%, the element has gone offscreen
    // We track this so that we can fire additional events for the same element
    if (hasEventFired.value && intersectionRatio === 0) {
      hasElBeenOffScreen.value = true;
    }
  }
};

return { __sfc: true,props, payload, context, snowplow, observer, timer, hasEventFired, hasElBeenOffScreen, hasError, observerCallback }
}

})